import { createStore } from "zustand/vanilla";
import type { GameResult } from "./connection";
import type { GameConnector, GameRoomHandle } from "./game-connection";
import { localizedReason } from "./join-error";

/** Every game's redacted view exposes whose turn it is (`turn`) and our own seat
 * (`you`) — enough to sound the "your turn" and win/lose cues generically. */
type SeatedView = { readonly you: number; readonly turn: number };

export type GameStatus = "connecting" | "playing" | "gameover" | "error";

export interface GameState<M, V> {
  status: GameStatus;
  view: V | null;
  /** The human's currently-legal moves, per the server (authoritative). */
  legal: readonly M[];
  result: GameResult | null;
  error: string | null;
  send: (move: M) => void;
  /** Signal the server the client finished loading (the loading bar filled) → it starts
   *  the bots. No-op until connected; ignored server-side for a resume/reconnect. */
  ready: () => void;
  /** «پایان بازی» — END the game server-side (drops its ActiveGame), so it no longer
   *  shows as resumable. Resolves only after the server ACKed that the row is gone;
   *  rejects on failure/timeout/not-connected — the caller must then keep the game
   *  listed and surface an error (NOT navigate away as if it succeeded). A plain
   *  {@link disconnect} leaves the game resumable. */
  endGame: () => Promise<void>;
  disconnect: () => void;
}

/**
 * Framework-free store for any solo game: connects via the injected connector and
 * mirrors the server's authoritative view. Testable with a fake connector — no
 * WebSocket. Every solo game (Hearts included, since the 2026-07-10 rebuild)
 * runs on this one store.
 */
export function createGameStore<M, V extends SeatedView>(connector: GameConnector<M, V>) {
  let handle: GameRoomHandle<M> | null = null;
  /**
   * THE GAME WAS LEFT BEFORE THE JOIN LANDED — the loading screen's exit. `disconnect` could only
   * leave a `handle` it already had, so an exit pressed while `joinOrCreate` was in flight did
   * nothing and the join landed afterwards into a store nobody renders: a lifecycle-managed room
   * on the save, and no one ever to leave it. Aborting tells the connector to stop (it never
   * manages that room), and a join that lands anyway is LEFT on arrival — consented, the opposite
   * of the online store's `drop`: a consented solo leave returns at once and keeps the save.
   */
  const left = new AbortController();

  const store = createStore<GameState<M, V>>(() => ({
    status: "connecting",
    view: null,
    legal: [],
    result: null,
    error: null,
    send: (move) => handle?.send(move),
    ready: () => handle?.ready(),
    endGame: async () => {
      // No handle (connection still opening / already torn down) must REJECT — a
      // silent no-op here would let the UI navigate away believing the end landed.
      if (!handle) throw new Error("not connected");
      await handle.end();
    },
    disconnect: () => {
      left.abort();
      handle?.leave();
      handle = null;
    },
  }));

  connector(
    {
      onState: (view, legal) => {
        const s = store.getState();
        // WHOLESALE replace: every state frame is the server's full truth. A frame
        // arriving after an error (a lifecycle reconnect re-emitted state) also
        // clears the stale error so the table recovers instead of sticking.
        // (No "your turn" chime: the beep was disliked and is gone — the table's own
        // card/deal cues carry the game.)
        store.setState(
          s.status === "gameover"
            ? { view, legal }
            : { view, legal, status: "playing", error: null },
        );
      },
      onGameOver: (result) => {
        // The win/lose chime is NOT played here: the summary overlay is held for a
        // beat so the final trick can sweep, so firing on the terminal frame put
        // the sound ~1.4s ahead of the visual. `useOutcomeSfx` plays it on reveal.
        store.setState({ result, status: "gameover" });
      },
      onError: (message) => store.setState({ status: "error", error: localizedReason(message) }),
    },
    left.signal,
  )
    .then((h) => {
      if (left.signal.aborted) {
        // It landed after the player left — nobody is here to hold it (see `left`).
        h.leave();
        return;
      }
      handle = h;
    })
    .catch((e: unknown) =>
      // Keep a localized server reason (e.g. the coming-soon gate); a raw network/
      // system string → null, so the container falls back to its own Persian
      // `fa.<game>.connectionError` (never a bare English error).
      store.setState({ status: "error", error: localizedReason(e) }),
    );

  return store;
}

export type GameStore<M, V extends SeatedView> = ReturnType<typeof createGameStore<M, V>>;
